import { useState, useEffect, useCallback } from 'react';
import { authService, type UserDto } from '../services/auth';

export const useAuth = () => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Debug: trace auth check on webapp
        console.log('[WebApp useAuth] checkAuth: starting auth check...');
        // Ask API who the current user is based on shared cookie (arrangelly_token)
        const currentUser = await authService.getCurrentUser();
        console.log('[WebApp useAuth] checkAuth: received user', JSON.stringify(currentUser, null, 2));
        if (currentUser && currentUser.id) {
          console.log('[WebApp useAuth] checkAuth: user is authenticated, setting user state');
          setUser(currentUser);
        } else {
          console.log('[WebApp useAuth] checkAuth: user is null or missing id, treating as unauthenticated');
          setUser(null);
        }
      } catch (err) {
        console.log('[WebApp useAuth] checkAuth: /auth/me failed, treating as unauthenticated', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    // Run once on mount
    checkAuth();
    
    // Listen for custom auth events (when login happens in same window)
    const handleAuthEvent = () => {
      checkAuth();
    };
    
    window.addEventListener('auth:login', handleAuthEvent);
    window.addEventListener('auth:logout', handleAuthEvent);
    
    // Also listen for storage changes (in case token is set from another tab/window of SAME origin)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);

    // Additionally, when the tab gains focus, re-check cookies.
    // This is important because login may happen on a different origin
    // (dashboard.arrangelly.local), and then the user returns to arrangelly.local.
    const handleWindowFocus = () => {
      console.log('[WebApp useAuth] window focus: re-checking auth state based on cookies');
      checkAuth();
    };
    window.addEventListener('focus', handleWindowFocus);

    // When navigating back using the browser back button, many browsers restore
    // the page from the back-forward cache (bfcache) without re-running JS.
    // The 'pageshow' event fires in that case, so we use it to re-check cookies.
    const handlePageShow = (event: any) => {
      // When coming from the browser back/forward cache (bfcache), the page
      // can be restored with stale JS state that doesn't see new cookies yet.
      // In that case, force a full reload once so cookies + auth state are fresh.
      if (event?.persisted) {
        console.log(
          '[WebApp useAuth] pageshow: restored from bfcache, forcing full reload to pick up latest cookies'
        );
        window.location.reload();
        return;
      }

      console.log(
        '[WebApp useAuth] pageshow: page shown (not from bfcache), re-checking auth state based on cookies'
      );
      checkAuth();
    };
    window.addEventListener('pageshow', handlePageShow);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth:login', handleAuthEvent);
      window.removeEventListener('auth:logout', handleAuthEvent);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      console.log('[WebApp useAuth] login: calling /auth/login...', { email });
      const response = await authService.login({ email, password });
      console.log('[WebApp useAuth] login: /auth/login response', response);
      // After successful login, fetch current user via cookie-backed /auth/me
      try {
        console.log('[WebApp useAuth] login: re-checking current user via /auth/me after login');
        const currentUser = await authService.getCurrentUser();
        console.log('[WebApp useAuth] login: /auth/me after login returned', JSON.stringify(currentUser, null, 2));
        if (currentUser && currentUser.id) {
          console.log('[WebApp useAuth] login: setting user state after successful login');
          setUser(currentUser);
          // Notify other useAuth instances (e.g. Header) that auth state changed
          window.dispatchEvent(new Event('auth:login'));
        } else {
          console.log('[WebApp useAuth] login: /auth/me returned invalid user, falling back to response.user');
          setUser(response.user);
          window.dispatchEvent(new Event('auth:login'));
        }
      } catch {
        // If for some reason /auth/me fails, fall back to user from login response
        console.log('[WebApp useAuth] login: /auth/me after login failed, falling back to response.user');
        setUser(response.user);
        window.dispatchEvent(new Event('auth:login'));
      }
      return { success: true };
    } catch (error: any) {
      console.log('[WebApp useAuth] login: failed', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  }, []);

  const register = useCallback(async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      console.log('[WebApp useAuth] register: calling /auth/register...', { email, firstName, lastName });
      const response = await authService.register({ email, password, firstName, lastName });
      try {
        console.log('[WebApp useAuth] register: re-checking current user via /auth/me after register');
        const currentUser = await authService.getCurrentUser();
        console.log('[WebApp useAuth] register: /auth/me after register returned', currentUser);
        setUser(currentUser);
      } catch {
        console.log('[WebApp useAuth] register: /auth/me after register failed, falling back to response.user');
        setUser(response.user);
      }
      return { success: true };
    } catch (error: any) {
      console.log('[WebApp useAuth] register: failed', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  }, []);

  const logout = useCallback(async () => {
    console.log('[WebApp useAuth] logout: calling /auth/logout and clearing local user');
    setUser(null);
    // Dispatch custom event to notify other useAuth instances
    window.dispatchEvent(new Event('auth:logout'));
    // authService.logout() will handle the API call and navigation
    await authService.logout();
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
  };
};

